/**
 * `/teams/:teamId` — one team: what is wrong with it, its details, its members.
 *
 * Problems come first, spelled out (leavers by name), because fixing them is
 * why a manager comes here (2 Oct). Edit controls show only for the team's
 * manager or an admin (the server's `can_edit`). All of IT may read every team
 * (8 Oct): for them the page is view only, with a short note saying who can
 * change it, and the server refuses their writes anyway.
 *
 * A count-only team ("End Users", everyone) shows its details (still
 * editable) and a member count, with a notice instead of the members table:
 * its members are managed in Ivanti (2 Oct).
 */

import { Icon, Pencil } from "@norican/icons";
import { Alert, Badge, Button, Card, Typography } from "@norican/react";
import { type ReactNode, useState } from "react";
import { useNavigate, useParams } from "react-router";

import {
  AppBreadcrumbs,
  ErrorState,
  LoadingRows,
  PageContainer,
  RelativeTime,
} from "@/components/common";
import { useDocumentTitle } from "@/hooks";
import { useT } from "@/i18n";
import { useMe } from "@/lib/api";
import { logger } from "@/lib/logger";

import { useTeam } from "./api";
import { memberCountState } from "./lib/team-count";
import { canEditTeam } from "./lib/team-problems";
import { TeamDetailsDialog } from "./team-details-dialog";
import { TeamMembersSection } from "./team-members-section";
import { TeamProblemList } from "./team-problems";

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <Typography variant="caption" tone="muted">
        {label}
      </Typography>
      <Typography as="div" variant="body-sm" className="break-all">
        {children}
      </Typography>
    </div>
  );
}

export function TeamDetailPage() {
  const { t } = useT();
  const { teamId = "" } = useParams();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const query = useTeam(teamId);
  const [editing, setEditing] = useState(false);

  const team = query.data;
  useDocumentTitle(team?.name ?? t("teams.title"));

  const isAdmin = me?.role === "admin";
  const canEdit = canEditTeam(team);
  const notSet = t("teams.notSet");
  const countState = team ? memberCountState(team) : undefined;

  return (
    <PageContainer
      title={team?.name ?? t("teams.title")}
      subtitle={
        team === undefined
          ? undefined
          : team.you_manage === true
            ? t("teams.detail.youManage")
            : canEdit
              ? undefined
              : t("teams.detail.viewOnly")
      }
      breadcrumbs={
        <AppBreadcrumbs
          items={[{ label: t("teams.title"), to: "/teams" }, { label: team?.name ?? "…" }]}
        />
      }
      width="wide"
      actions={
        canEdit ? (
          <Button
            variant="outline"
            tone="neutral"
            startIcon={<Icon icon={Pencil} size="sm" />}
            onClick={() => setEditing(true)}
          >
            {t("teams.detail.edit")}
          </Button>
        ) : null
      }
    >
      {query.isLoading ? (
        <LoadingRows rows={6} />
      ) : query.error != null || team === undefined ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <TeamProblemList problems={team.problems ?? []} />

          <Card variant="outline" padding="md" className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <Typography as="h2" variant="heading-4">
                {t("teams.detail.detailsTitle")}
              </Typography>
              {team.is_service_desk ? (
                <Badge tone="info" variant="subtle" size="sm">
                  {t("teams.serviceDesk")}
                </Badge>
              ) : null}
            </div>
            <div
              className={`grid gap-4 sm:grid-cols-2 ${team.count_only === true ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}
            >
              <DetailRow label={t("teams.detail.name")}>{team.name}</DetailRow>
              <DetailRow label={t("teams.detail.email")}>{team.email ?? notSet}</DetailRow>
              <DetailRow label={t("teams.detail.manager")}>
                {team.manager_name ?? notSet}
                {team.manager_email ? (
                  <Typography as="span" variant="caption" tone="muted" className="block">
                    {team.manager_email}
                  </Typography>
                ) : null}
              </DetailRow>
              <DetailRow label={t("teams.detail.lastChanged")}>
                {team.updated_at ? (
                  <RelativeTime value={team.updated_at} mode="absolute" />
                ) : (
                  notSet
                )}
              </DetailRow>
              {team.count_only === true ? (
                <DetailRow label={t("teams.column.members")}>
                  {countState?.kind === "known"
                    ? t("teams.memberCount", { count: countState.count })
                    : t("teams.countingMembers")}
                </DetailRow>
              ) : null}
            </div>
          </Card>

          {team.count_only === true ? (
            <Alert tone="info">{t("teams.detail.countOnlyNotice")}</Alert>
          ) : (
            <TeamMembersSection team={team} canEdit={canEdit} />
          )}

          {canEdit ? (
            <TeamDetailsDialog
              team={team}
              isAdmin={isAdmin}
              open={editing}
              onOpenChange={setEditing}
              onHandedOver={() => {
                logger.debug("[teams] handed over", team.id);
                void navigate("/teams");
              }}
            />
          ) : null}
        </>
      )}
    </PageContainer>
  );
}
